import { gql, useMutation } from '@apollo/client'
import cx from 'classnames'
import { useToast } from 'components/Dashboard/Toast'
import rollbar from 'helpers/rollbar'
import { capitalizeFirstLetter, formatDate } from 'helpers/string'
import { orderBy } from 'lodash'
import { SmsSentEventFragment } from './__generated__/SmsSentEventFragment'

export const smsSentEventFragment = gql`
  fragment SmsSentEventFragment on SmsSentEvent {
    messageText
    sms {
      id
      status
      viewerCanCancel
      viewerCanResend
      history {
        id
        createdAt
        actor
        action
        detail
      }
      twilioStatus
    }
  }
`

const abortOutboundSmsMutation = gql`
  mutation AbortOutboundSms($id: ID!, $reason: String!) {
    abortOutboundSms(id: $id, reason: $reason) {
      success
      message
      outboundSms {
        id
        status
      }
    }
  }
`

const ItemHistoryDisplay = ({ history }) => {
  if (history.action === 'ABORTED_BY_ADMIN') {
    return (
      <p>
        <strong>Canceled: </strong>
        {formatDate(history.createdAt)} by {history.actor} - &ldquo;
        {history.detail}&rdquo;
      </p>
    )
  }

  if (history.action === 'TWILIO_STATUS_UPDATE') {
    return (
      <p>
        <strong>{capitalizeFirstLetter(history.detail)}: </strong>
        {formatDate(history.createdAt)} (from Twilio)
      </p>
    )
  }

  return (
    <p>
      <strong>{formatDate(history.createdAt)}: </strong>
      {history.action}
    </p>
  )
}

const SmsSentEvent = ({ event }: { event: SmsSentEventFragment }) => {
  const [abortOutboundSms, { loading: canceling }] = useMutation(abortOutboundSmsMutation)
  const { addToast } = useToast()

  const canCancel = event.sms.viewerCanCancel
  const canResend = false // event.sms.viewerCanResend

  const statusDescriptions = {
    SMS_AWAITING_CONFIRMATION: 'Queued',
    SENDING: 'Sending to Twilio',
    SENT_TO_TWILIO: 'Sent to Twilio',
    ABORTED_BY_ADMIN: 'Canceled by admin user',
  }

  const onCancel = async () => {
    const reason = window.prompt('Please explain why this SMS should be canceled')

    if (!reason) {
      return
    }

    try {
      const result = await abortOutboundSms({
        variables: { id: event.sms.id, reason },
      })

      if (!result.data.abortOutboundSms.success) {
        addToast({
          title: 'Error',
          messageType: 'error',
          content: result.data.abortOutboundSms.message || 'Could not abort SMS',
        })
      }

      addToast({
        title: 'Success',
        messageType: 'success',
        content: 'Canceled SMS',
      })
    } catch (err) {
      rollbar.error('abortOutboundSms error', err)

      addToast({
        title: 'Error',
        messageType: 'error',
        content: `Failed to cancel, ${err.message}`,
      })
    }
  }

  const onResend = async () => {
    addToast({
      title: 'Sorry',
      messageType: 'error',
      content: 'Not implemented yet!',
    })
  }

  return (
    <div>
      <p>
        <strong>Status: </strong>
        {statusDescriptions[event.sms.status]}
      </p>
      {event.sms.twilioStatus && (
        <p>
          <strong>Twilio status: </strong>
          {event.sms.twilioStatus}
        </p>
      )}
      <p>
        <strong>Message: </strong>"{event.messageText}"
      </p>
      {orderBy(event.sms.history, (s) => s.createdAt, 'desc').map((history) => (
        <ItemHistoryDisplay key={history.id} history={history} />
      ))}

      {(canCancel || canResend) && (
        <div className="buttons is-right">
          {canResend && (
            <button className="button is-small is-info is-outlined" onClick={onResend}>
              Re-send
            </button>
          )}

          {canCancel && (
            <button
              className={cx('button is-small is-danger is-outlined', {
                'is-loading': canceling,
              })}
              onClick={onCancel}
            >
              Cancel
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default SmsSentEvent
